import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Switch } from '@/components/ui/switch.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import StatusPill from '@/components/tap/StatusPill.jsx';
import { getToken, getBatchTokens, setProgrammed } from '@/services/tap.service.js';
import { formatDateTime } from '@/lib/date-display.js';

// Single-token programming surface. Mobile-first, iPhone + NFC Tools workflow
// (NO WebNFC in Phase 1). Optimized for thumbs, glare, one-handed use: copy
// the URL → switch to NFC Tools → write → come back → flip the toggle → next.
const STEPS = [
  'Open NFC Tools → Write → Add a record → URL/URI',
  'Paste the URL',
  'Hold the chip to the back of your phone',
  'Come back here and flip the toggle below',
];

export default function TapProgram() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [findingNext, setFindingNext] = useState(false);
  const [showMeta, setShowMeta] = useState(false);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);
    setToken(null);
    getToken(id)
      .then((t) => { if (!aborted) { setToken(t); setLoading(false); } })
      .catch((e) => { if (!aborted) { setError(e); setLoading(false); } });
    return () => { aborted = true; };
  }, [id]);

  const programmed = !!token?.programmed_at;
  const isRevoked = token?.status === 'revoked';

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(token.url);
      toast.success('Copied — switch to NFC Tools');
    } catch {
      toast.error('Clipboard blocked — long-press the URL to copy');
    }
  }

  async function toggle(checked) {
    setToggling(true);
    try {
      await setProgrammed(id, checked);
      const fresh = await getToken(id);
      setToken(fresh);
      toast.success(checked ? 'Marked programmed' : 'Unmarked programmed');
    } catch (e) {
      toast.error(`Couldn't update: ${e.body?.error || e.code || e.message}`);
    } finally {
      setToggling(false);
    }
  }

  async function next() {
    setFindingNext(true);
    try {
      const d = await getBatchTokens(token.batch_label);
      const nxt = (d.tokens || []).find(
        (t) => !t.programmed_at && t.status !== 'revoked' && t.id !== id
      );
      if (nxt) navigate(`/admin/tap/program/${nxt.id}`);
      else toast.success('No more unprogrammed tokens in this batch.');
    } catch (e) {
      toast.error(`Couldn't load batch: ${e.code || e.message}`);
    } finally {
      setFindingNext(false);
    }
  }

  if (loading) return <div className="p-4 text-sm text-text-muted">Loading…</div>;
  if (error || !token) {
    return (
      <div className="p-4">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} strokeWidth={1.5} />
        </Button>
        <p className="mt-4 text-sm text-accent">
          Couldn't load this token{error ? `: ${error.code || error.message}` : ''}.
        </p>
      </div>
    );
  }

  const utmTuple = [token.utm_source, token.utm_medium, token.utm_campaign, token.utm_content]
    .map((v) => v || '–').join(' / ');

  return (
    <div className="mx-auto max-w-lg p-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Back to batch"
          onClick={() => navigate(`/admin/tap/batch/${encodeURIComponent(token.batch_label)}`)}
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
        </Button>
        <div className="min-w-0">
          <div className="truncate text-sm text-text-muted">{token.batch_label}</div>
          <div className="font-mono text-xs text-text-dim">…{token.id.slice(-8)}</div>
        </div>
        <StatusPill status={token.status} className="ml-auto" />
      </div>

      {isRevoked && (
        <div className="mt-4 rounded-card border border-accent/40 bg-accent-soft p-3 text-sm text-accent">
          This token is revoked — it can't be programmed or handed out.
        </div>
      )}

      {/* Big URL display */}
      <button
        type="button"
        onClick={copyUrl}
        className="mt-4 block w-full select-all break-all rounded-card border border-border bg-surface-strong/50 p-4 text-left font-mono text-base text-text"
      >
        {token.url}
      </button>
      <Button className="mt-2 w-full" onClick={copyUrl}>
        <Copy size={16} strokeWidth={1.5} /> Copy URL
      </Button>

      {/* NFC Tools instructions */}
      <ol className="mt-6 space-y-2">
        {STEPS.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm text-text-muted">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-border/60 text-xs font-semibold text-text">
              {i + 1}
            </span>
            <span className="pt-0.5">{s}</span>
          </li>
        ))}
      </ol>

      {/* Mark programmed toggle */}
      <div className="mt-6 flex items-center justify-between rounded-card border border-border bg-surface p-4">
        <div>
          <div className="text-base font-medium text-text">Mark programmed</div>
          <div className="text-xs text-text-muted">Flip after writing the chip in NFC Tools.</div>
        </div>
        <Switch
          checked={programmed}
          onCheckedChange={toggle}
          disabled={toggling || isRevoked}
          aria-label="Mark programmed"
          className="h-8 w-14"
        />
      </div>

      {/* Next unprogrammed */}
      <Button
        variant="outline"
        className="mt-4 w-full"
        onClick={next}
        disabled={findingNext || isRevoked}
      >
        {findingNext ? 'Finding…' : 'Next unprogrammed in batch'}
        <ArrowRight size={16} strokeWidth={1.5} />
      </Button>

      {/* Collapsible meta footer */}
      <button
        type="button"
        onClick={() => setShowMeta((v) => !v)}
        className="mt-6 flex w-full items-center gap-1.5 text-sm text-text-muted hover:text-text"
      >
        {showMeta ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        Token details
      </button>
      {showMeta && (
        <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 rounded-card border border-border bg-surface p-3 text-xs">
          <dt className="text-text-dim">Token id</dt>
          <dd className="break-all font-mono text-text">{token.id}</dd>
          <dt className="text-text-dim">Batch</dt>
          <dd className="text-text">{token.batch_label}</dd>
          <dt className="text-text-dim">UTM s/m/c/c</dt>
          <dd className="break-all text-text">{utmTuple}</dd>
          <dt className="text-text-dim">Minted</dt>
          <dd className="text-text">{token.issued_at ? formatDateTime(token.issued_at) : '—'}</dd>
        </dl>
      )}
    </div>
  );
}
